import { useEffect } from "react";
import { toast } from "sonner";
import { interfaceTranslator } from "@/i18n/translate";
import { useAppSession } from "@/lib/offline-session";
import {
  type FlushResult,
  flushRegistrationOutbox,
  type QueuedCapture,
} from "@/lib/registration-outbox";

/**
 * A capture that did not file means a guest is legally unregistered against a
 * deadline, so those toasts outlive the default few seconds — the host may not
 * be looking at the phone the moment the queue drains on reconnect.
 */
const ATTENTION_TOAST_MS = 30_000;

/**
 * How many names to spell out before falling back to a count: the catalog's
 * guest-list messages have a slot for each, so this and they change together.
 */
const MAX_NAMES = 2;

/**
 * Name the guests a message is about. "A guest registration couldn't be filed"
 * is unactionable for a host with six arrivals — they need to know who to go
 * and fix. Past a couple of names the list stops being readable, so it becomes
 * a remainder.
 *
 * Only guests the reader may see are named: their own captures, and guests in
 * their own replica (`visibleGuestIds`), which the sync rules fill with exactly
 * the guests their role lets them see. The queue is per device, not per
 * account, so on a phone shared across properties it holds other accounts'
 * captures, and those guests' names must not reach whoever signs in next. They
 * are counted instead.
 */
function describeGuests(
  entries: QueuedCapture[],
  { viewerId, visibleGuestIds }: Visibility
): string {
  const names = entries
    .filter(
      (entry) =>
        entry.capturedByUserId === viewerId ||
        visibleGuestIds.has(entry.guestId)
    )
    .map((entry) => entry.guestName)
    .filter(Boolean)
    .slice(0, MAX_NAMES);
  const rest = entries.length - names.length;
  const t = interfaceTranslator("checkIn.useRegistrationOutbox");
  const [first, second] = names;
  if (!first) {
    return t("guestsUnnamed", { count: rest });
  }
  if (rest === 0) {
    return second ? t("guestsTwoNames", { first, second }) : first;
  }
  return second
    ? t("guestsTwoNamesAndOthers", { count: rest, first, second })
    : t("guestsOneNameAndOthers", { count: rest, first });
}

interface Visibility {
  viewerId: string;
  visibleGuestIds: ReadonlySet<string>;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The guests among `ids` that are in this device's replica. Loaded lazily:
 * this hook sits in the pre-login bundle, and PowerSync is already loaded by
 * the time anyone is signed in. A failed read names nobody rather than
 * dropping the report: the report is the only notice a refused capture gets.
 */
async function readVisibleGuestIds(
  ids: string[],
  viewerId: string
): Promise<ReadonlySet<string>> {
  if (ids.length === 0) {
    return new Set();
  }
  try {
    // Only a replica already verified as the viewer's says anything about
    // what they may see. The hook mounts behind that check today; this keeps
    // a future move of it from reading another account's guests.
    const { isReplicaReloadPending, readReplicaOwner } = await import(
      "@/lib/device-replica"
    );
    const isViewersReplica =
      readReplicaOwner() === viewerId && !isReplicaReloadPending();
    if (!isViewersReplica) {
      return new Set();
    }
    const { powerSyncDb } = await import("@/lib/powersync");
    const placeholders = ids.map(() => "?").join(", ");
    const rows = await powerSyncDb.getAll<{ id: string }>(
      `SELECT id FROM guests WHERE id IN (${placeholders})`,
      ids
    );
    return new Set(rows.map(({ id }) => id));
  } catch (err) {
    console.error("[registration] could not read visible guests:", err);
    return new Set();
  }
}

/**
 * Tell the host what the flush did. Each outcome is a different ask of them:
 * nothing (filed), fix it yourself (dropped), or chase a colleague (deferred).
 */
function report(
  { sent, dropped, deferred }: FlushResult,
  visibility: Visibility
): void {
  const t = interfaceTranslator("checkIn.useRegistrationOutbox");
  if (sent > 0) {
    toast.success(t("filed", { count: sent }));
  }
  // A capture refused for good leaves the queue, so this is the only moment the
  // host can learn those guests were never registered. Saying nothing here
  // would recreate the silence this whole flow exists to end — just later, and
  // against a deadline that may already have run.
  if (dropped.length > 0) {
    // Deliberately not "use Review details": that control only renders for a
    // filing rejected on its data, and a capture can be refused for reasons
    // that leave the filing untouched. Point at the reservation, which is
    // always there, rather than a button that may not be.
    toast.error(
      t("dropped", {
        count: dropped.length,
        guests: describeGuests(dropped, visibility),
      }),
      { duration: ATTENTION_TOAST_MS }
    );
  }
  // Someone else's scan is stuck on this device. Naming that is what lets a
  // shift lead do something about it — ask them to sign in, or re-scan the
  // guest — instead of the capture quietly ageing out.
  if (deferred.length > 0) {
    const guests = describeGuests(deferred, visibility);
    toast.warning(
      t("deferred", {
        count: deferred.length,
        guests: deferred.length === 1 ? capitalize(guests) : guests,
      }),
      { duration: ATTENTION_TOAST_MS }
    );
  }
}

/**
 * Drain captures parked by a failed registration POST: once on mount (the scan
 * may have been taken in a previous session) and again whenever the device
 * comes back online.
 *
 * Does nothing until someone is signed in. `App` is mounted on the signed-out
 * routes too, and a flush with no identity owns none of the queue: every entry
 * would be reported as "taken by another sign-in", printing guests' names onto
 * the login screen of a shared front-desk tablet and telling the host something
 * untrue. There is also nothing to send, since the retry is an authenticated
 * write.
 *
 * Only this session's own captures are sent. The server attests a registration
 * to whoever posts it, so a colleague's queued scan is left for them rather
 * than signed with the wrong name (`useAppSession` resolves the identity
 * offline too, when an older bundle queued them).
 */
export function useRegistrationOutbox(): void {
  const { data: session } = useAppSession();
  const userId = session ? session.user.id : null;

  useEffect(() => {
    if (!userId) {
      return;
    }
    let cancelled = false;

    const flush = async () => {
      try {
        const result = await flushRegistrationOutbox(userId);
        const visibleGuestIds = await readVisibleGuestIds(
          [...result.dropped, ...result.deferred].map(({ guestId }) => guestId),
          userId
        );
        if (cancelled) {
          return;
        }
        report(result, { viewerId: userId, visibleGuestIds });
      } catch (err) {
        // Retriable entries stay queued for the next flush; nothing to tell the
        // host until one is sent or dropped.
        console.error("[registration] outbox flush failed:", err);
      }
    };

    void flush();
    window.addEventListener("online", flush);
    return () => {
      cancelled = true;
      window.removeEventListener("online", flush);
    };
  }, [userId]);
}
